import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import type {
  Airport,
  BookedFlightItineraryWithDepartureTime,
} from "@b2bportal/air-booking-api";
import {
  type DisruptionRebookShopRequest,
  DisruptionRebookShopResponseEnum,
  type TripDetailsV2,
  type Fare,
  type Flights,
  type FlightsFareSlice,
  type Slice,
  type Trip,
} from "@b2bportal/air-disruption-api";
import {
  DisruptionExerciseContainer,
  DisruptionExerciseFooter,
  DisruptionExercisePage,
  DisruptionExercisePageContent,
  disruptionExerciseFlags,
} from "@b2bportal/core-disruption";
import {
  IconNameEnum,
  useDisruptionStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  type AirlineCode,
  type AppDispatch,
  CoreDisruptionComponent,
  type ITimeRange,
} from "@b2bportal/core-types";
import { Dialog, DialogContent, Icon } from "@b2bportal/core-ui";
import { useDeviceType } from "@b2bportal/core-utilities";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  DisruptionExerciseFlightCard,
  DisruptionExerciseDateFilter,
  DisruptionExerciseFlightInfoRow,
  DisruptionExerciseShopFilter,
  type RebookAirlineOptions,
} from "@tenant/disruption";
import clsx from "clsx";
import dayjs from "dayjs";
import { disruptionRebookShop } from "../../../../features/exercise";
import { getHasCheckedBags } from "../../../../features/exercise/store/selectors";
import {
  DisruptionExerciseProgress,
  resetDisruptionExerciseState,
  setDisruptionExerciseProgress,
} from "../../../../features/exercise/store/slice";
import type { FetchTripDetailsThunk } from "../../../../types";
import { DisruptionExerciseNoFlightsAvailable } from "../../components/DisruptionExerciseNoFlightsAvailable";
import { DisruptionExercisePageWidth } from "../../components/DisruptionExercisePage/DisruptionExercisePage";
import defaultStyles from "./DisruptionExerciseRebookFlightShopPage.module.scss";

export interface DisruptionExerciseRebookFlightShopPageProps {
  disruptedFlight: BookedFlightItineraryWithDepartureTime;
  contractId: string;
  origin: string;
  destination: string;
  departure: string;
  airports: { [key: string]: Airport };
  setShoppedFlight: (flightInfo: {
    selectedFare: Fare;
    selectedTrip: Trip;
    selectedTripSlice: Slice;
    selectedFareSlice: FlightsFareSlice;
    selectedTripDetails?: TripDetailsV2;
  }) => void;
  bookingReturn: boolean;
  termsLink: string;
  delayHours: string;
  hoursString: string;
  onSupportClick: () => void;
  handleRefundClick: () => void;
  cancelFlowAction?: () => void;
  fetchTripDetails: FetchTripDetailsThunk;
  showFlightCardDetails?: boolean;
  customFilter?: (slice: Slice) => boolean;
}

const config = {
  INITIAL_RESULT_SET_SIZE: 20,
  SHOW_MORE_NUM: 20,
  DESKTOP_OFFSET_SCROLL: 150,
  MOBILE_OFFSET_SCROLL: 150,
};

export const DisruptionExerciseRebookFlightShopPage = ({
  disruptedFlight,
  contractId,
  origin,
  destination,
  departure,
  airports,
  setShoppedFlight,
  bookingReturn,
  termsLink,
  delayHours,
  hoursString,
  onSupportClick,
  handleRefundClick,
  cancelFlowAction,
  fetchTripDetails,
  showFlightCardDetails = false,
  customFilter,
}: DisruptionExerciseRebookFlightShopPageProps) => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const dispatch = useDispatch<AppDispatch>();
  const hasCheckedBags = useSelector(getHasCheckedBags);
  const COMPONENT_KEY =
    CoreDisruptionComponent.DisruptionExerciseRebookFlightShopPage;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const [flights, setFlights] = useState<Flights>();
  const [rebookShopError, setRebookShopError] = useState<boolean>(false);
  const today = dayjs(departure);
  const tomorrow = today.add(1, "day");
  const [showTodayFlights, setShowTodayFlights] = useState<boolean>(true);
  const fareName =
    disruptedFlight.bookedItinerary.travelItinerary.slices[0].segments[0]
      .cabinClassName;
  const numberOfTravelers =
    disruptedFlight.bookedItinerary.passengers.alone.length +
    disruptedFlight.bookedItinerary.passengers.withLapInfants.length;
  const [loading, setLoading] = useState<boolean>(false);
  const [hideFloatingButton, setHideFloatingButton] = useState<boolean>(true);
  const [selectedStops, setSelectedStops] = useState<Array<number>>([]);
  const [selectedAirline, setSelectedAirlines] = useState<Array<string>>([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);
  const refs = useRef<Array<React.RefObject<HTMLDivElement>>>([]);
  const [expandedFlightCard, setExpandedFlightCard] = useState<string>();

  const { isEligibleToBookReturn } = disruptionExerciseFlags(
    disruptedFlight.ancillaries.disruptionProtection
  );

  const TIME_RANGE_MAX = 1439;
  const initialTimeRange: ITimeRange = {
    min: 0,
    // note: time measured in minutes
    max: TIME_RANGE_MAX,
  };

  const [departureTimeRange, setDepartureTimeRange] =
    useState<ITimeRange>(initialTimeRange);
  const [arrivalTimeRange, setArrivalTimeRange] =
    useState<ITimeRange>(initialTimeRange);
  const [layoverDuration, setLayoverDuration] = useState<ITimeRange>({
    min: 0,
    max: 24,
  });
  const [selectedFlightDuration, setSelectedFlightDuration] =
    useState<ITimeRange>({ min: 0, max: 24 });

  const getCityName = (code?: string) => {
    return code ? airports[code]?.cityName || "" : "";
  };

  const handleGoBack = () => {
    if (bookingReturn) {
      dispatch(
        setDisruptionExerciseProgress(DisruptionExerciseProgress.NotStarted)
      );
    } else {
      dispatch(
        setDisruptionExerciseProgress(DisruptionExerciseProgress.RebookReview)
      );
    }
  };

  const handleDateChange = useCallback((isToday: boolean) => {
    setShowTodayFlights(isToday);
  }, []);

  const handleAirlineSelection = useCallback(
    (selected: boolean, airline: RebookAirlineOptions) => {
      if (selected) {
        const newArray = selectedAirline.slice();
        newArray.push(airline.value);
        setSelectedAirlines(newArray);
      } else {
        const newArray = selectedAirline.filter((element) => {
          return element !== airline.value;
        });
        setSelectedAirlines(newArray);
      }
      handleDateChange(showTodayFlights);
    },
    [selectedAirline, handleDateChange, showTodayFlights]
  );

  const handleStopsSelection = useCallback(
    (selected: boolean, numberOfStops: number) => {
      if (selected) {
        const newArray = selectedStops.slice();
        newArray.push(numberOfStops);
        setSelectedStops(newArray);
      } else {
        const newArray = selectedStops.filter((element) => {
          return element !== numberOfStops;
        });
        setSelectedStops(newArray);
      }
      handleDateChange(showTodayFlights);
    },
    [selectedStops, handleDateChange, showTodayFlights]
  );

  const handleFlightDurationChange = useCallback((min: number, max: number) => {
    setSelectedFlightDuration({ min: min, max: max });
  }, []);

  const handleDepartureTimeChange = useCallback((min: number, max: number) => {
    setDepartureTimeRange({ min: min, max: max });
  }, []);

  const handleArrivalTimeChange = useCallback((min: number, max: number) => {
    setArrivalTimeRange({ min: min, max: max });
  }, []);

  const handleLayoverDurationChange = useCallback(
    (min: number, max: number) => {
      setLayoverDuration({ min: min, max: max });
    },
    []
  );

  const handleDismissMobileFilter = useCallback(() => {
    setMobileFilterOpen(false);
  }, []);

  const handleOpenMobileFilter = useCallback(() => {
    setMobileFilterOpen(true);
  }, []);

  const airlinesOptions = () => {
    if (flights) {
      const airlines = new Set<AirlineCode>();
      const allAirlines: RebookAirlineOptions[] = [];
      flights.outbound.forEach((flight) => {
        const flightSlice = flights.slices[flight.slice];
        const airlineCode = flightSlice?.marketingAirline;
        if (!airlines.has(airlineCode)) {
          allAirlines.push({
            value: airlineCode,
            label: flights?.airlines[airlineCode]?.displayName || "",
          });
        }
        airlines.add(airlineCode);
      });
      return allAirlines;
    }
  };

  const stopsOptions = () => {
    if (flights) {
      const stops = new Set<number>();
      const allStops: number[] = [];

      flights.outbound.forEach((flight) => {
        const flightSlice = flights.slices[flight.slice];
        const stopCount = flightSlice.segments.length - 1;
        if (!stops.has(stopCount)) {
          allStops.push(stopCount);
        }
        stops.add(stopCount);
      });
      return allStops.sort((elm1, elm2) => elm1 - elm2);
    }
  };

  const flightDurationRange = () => {
    if (flights) {
      const durations = new Set<number>();
      const allDurations: number[] = [];

      flights.outbound.forEach((flight) => {
        const flightSlice = flights.slices[flight.slice];
        const durationInHours = Math.round(
          flightSlice.totalDurationMinutes / 60
        );
        if (!durations.has(durationInHours)) {
          allDurations.push(durationInHours);
        }
        durations.add(durationInHours);
      });

      return {
        min: Math.min(...allDurations),
        // note: time measured in minutes
        max: Math.max(...allDurations),
      };
    }
  };

  const getLayoverDuration = (flight: Slice) => {
    if (flight.segments.length > 1) {
      return flight.segments
        .map((segment) => {
          return segment.stopoverDurationMinutes
            ? segment.stopoverDurationMinutes
            : 0;
        })
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    } else {
      return 0;
    }
  };

  const getLayoverDurationRange = () => {
    if (flights) {
      const durations = new Set<number>();
      const allDurations: number[] = [];

      flights.outbound.forEach((flight) => {
        const duration = getLayoverDuration(flights.slices[flight.slice]);
        const durationInHours = Math.round(duration / 60);
        if (!durations.has(durationInHours)) {
          allDurations.push(durationInHours);
        }
        durations.add(durationInHours);
      });

      return {
        min: Math.min(...allDurations),
        max: Math.max(...allDurations),
      };
    }
  };

  const fetchDisruptionRebookShop = useCallback(() => {
    if (contractId && origin && destination) {
      setLoading(true);
      const req: DisruptionRebookShopRequest = {
        contractId,
        origin,
        destination,
        hasCheckedBags,
      };
      dispatch(disruptionRebookShop(req))
        .then((response) => {
          const data = unwrapResult(response);
          setLoading(false);
          if (
            data &&
            data.DisruptionRebookShopResponse ===
              DisruptionRebookShopResponseEnum.RebookShopSuccess
          ) {
            setFlights(data.flights);
          } else {
            setRebookShopError(true);
          }
        })
        .catch((error) => {
          setLoading(false);
          setRebookShopError(true);
        });
    }
  }, [
    setRebookShopError,
    setFlights,
    setLoading,
    contractId,
    origin,
    destination,
    hasCheckedBags,
    dispatch,
  ]);

  const performTimeRangeFilter = (
    flight: Slice,
    departureTime: ITimeRange,
    arrivalTime: ITimeRange
  ) => {
    const flightDeparture = dayjs(removeTimezone(flight.departure));
    const flightArrival = dayjs(removeTimezone(flight.arrival));

    const flightDepartureMinutes =
      flightDeparture.hour() * 60 + flightDeparture.minute();
    const flightArrivalMinutes =
      flightArrival.hour() * 60 + flightArrival.minute();

    const validDeparture =
      departureTime.max >= flightDepartureMinutes &&
      flightDepartureMinutes >= departureTime.min;
    const validArrival =
      arrivalTime.max >= flightArrivalMinutes &&
      flightArrivalMinutes >= arrivalTime.min;

    return validDeparture && validArrival;
  };

  const performLayoverTimeRangeFilter = useCallback(
    (flight: Slice, layoverRange: ITimeRange) => {
      if (flight.segments.length > 1) {
        const layoverInMinutes = getLayoverDuration(flight);
        const minRangeInMinutes = layoverRange.min * 60;
        const maxRangeInMinutes = layoverRange.max * 60;

        const validLayover =
          maxRangeInMinutes >= layoverInMinutes &&
          layoverInMinutes >= minRangeInMinutes;

        return validLayover;
      } else {
        return true;
      }
    },
    []
  );

  const performFlightDurationRangeFilter = useCallback(
    (flight: Slice, durationRange: ITimeRange) => {
      const flightDurationInMinutes = flight.totalDurationMinutes;
      const minDurationInMinutes = durationRange.min * 60;
      const maxDurationInMinutes = durationRange.max * 60;

      const validDuration =
        maxDurationInMinutes >= flightDurationInMinutes &&
        flightDurationInMinutes >= minDurationInMinutes;

      return validDuration;
    },
    []
  );

  useEffect(() => {
    fetchDisruptionRebookShop();
  }, [fetchDisruptionRebookShop]);

  const flightsToShow = useMemo(() => {
    if (flights) {
      const slices = flights?.outbound;
      return slices.filter((flight) => {
        const slice = flights?.slices[flight.slice];
        const departure = dayjs(slice.departure);

        // day filter
        const dayCondition = departure.isSame(
          showTodayFlights ? today : tomorrow,
          "day"
        );

        // stops filter
        const stops = slice.segments.length - 1;
        const stopsCondition =
          selectedStops.length > 0 ? selectedStops.includes(stops) : true;

        // airline filter
        const airline = slice.marketingAirline;
        const airlineCondition =
          selectedAirline.length > 0 ? selectedAirline.includes(airline) : true;

        // departure/arrival time filter
        const timeFilterCondition = performTimeRangeFilter(
          slice,
          departureTimeRange,
          arrivalTimeRange
        );

        const flightDurationCondition = performFlightDurationRangeFilter(
          slice,
          selectedFlightDuration
        );

        // layover duration
        const layoverDurationCondition = performLayoverTimeRangeFilter(
          slice,
          layoverDuration
        );

        const customFilterCondition = customFilter ? customFilter(slice) : true;

        return (
          dayCondition &&
          stopsCondition &&
          airlineCondition &&
          timeFilterCondition &&
          flightDurationCondition &&
          layoverDurationCondition &&
          customFilterCondition
        );
      });
    } else {
      return [];
    }
  }, [
    selectedAirline,
    flights,
    selectedStops,
    showTodayFlights,
    today,
    tomorrow,
    departureTimeRange,
    arrivalTimeRange,
    selectedFlightDuration,
    performLayoverTimeRangeFilter,
    layoverDuration,
    performFlightDurationRangeFilter,
    customFilter,
  ]);

  const onContinue = useCallback(() => {
    dispatch(
      setDisruptionExerciseProgress(
        DisruptionExerciseProgress.RebookConfirmation
      )
    );
  }, [dispatch]);

  const onFlightSelect = useCallback(
    (props: {
      fareSliceId: string;
      fareId: string;
      tripId: string;
      tripDetails?: TripDetailsV2;
    }) => {
      if (flights) {
        const fareSlice = flights.fareSlices[props.fareSliceId];
        setShoppedFlight({
          selectedFare: flights.fares[props.fareId],
          selectedTrip: flights.trips[props.tripId],
          selectedTripSlice:
            flights.slices[flights.trips[props.tripId].outbound],
          selectedFareSlice: fareSlice,
          selectedTripDetails: props.tripDetails,
        });

        if (showFlightCardDetails) {
          setHideFloatingButton(false);
          return;
        }
        !isDesktopAndUp ? setHideFloatingButton(false) : onContinue();
      }
    },
    [
      flights,
      setShoppedFlight,
      showFlightCardDetails,
      isDesktopAndUp,
      onContinue,
    ]
  );

  const todayTitle = !isDesktopAndUp
    ? today.format("ddd, MMM D")
    : t(
        "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.todayButton",
        {
          date: today.format("ddd, MMM D"),
        }
      );

  const tomorrowTitle = !isDesktopAndUp
    ? tomorrow.format("ddd, MMM D")
    : t(
        "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.tomorrowButton",
        {
          date: tomorrow.format("ddd, MMM D"),
        }
      );

  const dateButtons =
    flights?.outbound == null ? null : (
      <DisruptionExerciseDateFilter
        defaultSelection={showTodayFlights ? "today" : "tomorrow"}
        handleDateChange={handleDateChange}
        handleOpenMobileFilter={handleOpenMobileFilter}
        todayTitle={todayTitle}
        tomorrowTitle={tomorrowTitle}
      />
    );

  const scrollToView = (ref: React.RefObject<HTMLDivElement> | undefined) => {
    setTimeout(() => {
      const el = ref?.current;
      if (el) {
        const OFFSET = !isDesktopAndUp
          ? config.MOBILE_OFFSET_SCROLL
          : config.DESKTOP_OFFSET_SCROLL;
        const cardTop = el.getBoundingClientRect().top || 0;
        window.scrollBy({
          top: cardTop - OFFSET,
          behavior: "smooth",
        });
      }
    }, 200);
  };

  const refList = useMemo(() => {
    const list = refs.current;
    const flightListLen = flightsToShow.length;
    while (list.length < flightListLen) {
      list.push(createRef<HTMLDivElement>());
    }
    if (list.length > flightListLen) {
      list.length = flightListLen;
    }
    return list;
  }, [flightsToShow.length]);

  const flightCards = flightsToShow?.map((flight, index) => {
    if (flights) {
      const flightData = {
        sliceId: flight.slice,
        fares: flight.fares,
      };
      const ref = refList[index];
      const isSelected = expandedFlightCard === flight.slice;
      return (
        <DisruptionExerciseFlightCard
          key={flight.slice}
          flight={flightData}
          isSelected={isSelected}
          onClick={() => {
            if (expandedFlightCard === flight.slice) {
              setExpandedFlightCard("");
            } else {
              setExpandedFlightCard(flight.slice);
              scrollToView(ref);
            }
          }}
          sliceIndex={0}
          onFareSelect={(params: {
            sliceId: string;
            fareId: string;
            tripId: string;
          }) => {
            onFlightSelect({
              fareSliceId: params.sliceId,
              fareId: params.fareId,
              tripId: params.tripId,
              tripDetails: undefined,
            });
          }}
          goToNextStep={onContinue}
          ref={ref}
          flights={flights}
          fetchTripDetails={fetchTripDetails}
          airports={airports}
        />
      );
    }
  });

  const flightRows = (
    <div className={cn("disruptionExerciseSelectFlightContent")}>
      {dateButtons}
      <div className={cn("flight-list")}>{flightCards}</div>
    </div>
  );

  const filtersContent = (
    <DisruptionExerciseShopFilter
      origin={getCityName(origin)}
      destination={getCityName(destination)}
      stopsOptions={stopsOptions() ?? []}
      selectedStops={selectedStops}
      airlinesOptions={airlinesOptions() ?? []}
      selectedAirlines={selectedAirline}
      handleAirlineSelection={handleAirlineSelection}
      handleStopsSelection={handleStopsSelection}
      departureTimeRange={departureTimeRange}
      arrivalTimeRange={arrivalTimeRange}
      handleDepartureTimeChange={handleDepartureTimeChange}
      handleArrivalTimeChange={handleArrivalTimeChange}
      flightDurationRange={
        flightDurationRange() ?? {
          min: 0,
          max: 1,
        }
      }
      handleFlightDurationChange={handleFlightDurationChange}
      layoverDurationRange={
        getLayoverDurationRange() ?? {
          min: 0,
          max: 1,
        }
      }
      handleLayoverDurationChange={handleLayoverDurationChange}
      selectedLayoverDurationRange={layoverDuration}
    />
  );

  const filters = !isDesktopAndUp
    ? flights && (
        <Dialog open={mobileFilterOpen} onClose={handleDismissMobileFilter}>
          <DialogContent>
            {
              <div className={cn("shopMobileFiltersContainer")}>
                {filtersContent}
              </div>
            }
          </DialogContent>
        </Dialog>
      )
    : flights && (
        <div className={cn("desktopFiltersContainer")}>{filtersContent}</div>
      );

  return (
    <DisruptionExercisePage
      className={block}
      pageWidth={DisruptionExercisePageWidth.Large}
      title={loading ? "" : t("core-disruption.productName")}
      onBack={handleGoBack}
      openErrorModal={rebookShopError}
      onCloseErrorModal={() => {
        setRebookShopError(false);
        dispatch(resetDisruptionExerciseState());
      }}
      onSupportClick={onSupportClick}
      handleRefundClick={handleRefundClick}
      submitButtonHidden={hideFloatingButton}
      submitButtonAction={onContinue}
      submitButtonTitle={t(
        "core-disruption.disruptionUniversalExercise.continue"
      )}
      isLoading={loading}
      loadingMessage={t(
        "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.loadingMessage"
      )}
      rightNavElement={
        !isDesktopAndUp ? (
          flights ? (
            <Icon
              iconName={IconNameEnum.filter}
              className={cn("filterIcon")}
              onClick={handleOpenMobileFilter}
            />
          ) : undefined
        ) : undefined
      }
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
      onCancelFlow={cancelFlowAction}
      content={
        loading ? (
          <></>
        ) : (
          <DisruptionExercisePageContent
            topContent={
              <>
                <div className={cn("title")}>
                  {t(
                    "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.title"
                  )}
                </div>
                <DisruptionExerciseFlightInfoRow
                  originCode={origin}
                  destinationCode={destination}
                  numberOfTravelers={numberOfTravelers}
                  date={departure}
                  fareName={fareName ?? ""}
                  airports={airports}
                />
              </>
            }
            leftContent={
              <div className={cn("flightListContainer")}>
                {flightsToShow?.length > 0 ? (
                  <DisruptionExerciseContainer
                    content={[
                      flightRows,
                      <DisruptionExerciseFooter
                        onSupportClick={onSupportClick}
                        onRefundClick={
                          isEligibleToBookReturn ? undefined : handleRefundClick
                        }
                      ></DisruptionExerciseFooter>,
                    ]}
                    key={"disruption-rebook-flight-rows"}
                  ></DisruptionExerciseContainer>
                ) : (
                  <DisruptionExerciseContainer
                    content={[
                      dateButtons ?? <></>,
                      <DisruptionExerciseNoFlightsAvailable
                        onSupportClick={onSupportClick}
                        onRefundClick={handleRefundClick}
                      ></DisruptionExerciseNoFlightsAvailable>,
                    ]}
                    key={"disruption-rebook-no-flights-available"}
                  />
                )}
              </div>
            }
            rightConent={filters}
          />
        )
      }
    ></DisruptionExercisePage>
  );
};

export default DisruptionExerciseRebookFlightShopPage;
